/**
 * Page-scoped translations for the Home page and the portal shell it introduces
 * (sidebar / topbar / footer). Loaded as a local i18n scope in Home.vue and
 * LayoutPortal.vue — generic keys still resolve from the global catalog.
 */
const en = {
  nav: { home: 'Home', subscriptions: 'Subscriptions', orders: 'Orders', invoices: 'Invoices', partners: 'Partners' },
  shell: {
    customerArea: 'Customer area',
    logout: 'Log out',
    accountMenu: 'Account menu',
    signedInAs: 'Signed in as',
    store: 'Invoxia store',
    support: 'Support',
  },
  footer: {
    tagline: 'Invoxia customer area',
    terms: 'Terms & conditions',
    privacy: 'Privacy',
    support: 'Help & support',
  },
  home: {
    welcome: 'Welcome to your Invoxia space',
    subtitle: 'Keep an eye on what matters. Find your devices, plans, orders and invoices, all in one place.',
    yourDevice: 'Your device',
    yourDevices: 'Your devices',
    manageSubscriptions: 'Manage my subscriptions',
    quickAccess: 'Quick access',
    devicesTracked: 'devices tracked',
    seeAll: 'See all',
  },
  onboarding: {
    heading: 'Activate your first device',
    body: 'Take your tracker out of its box, open the Invoxia app and follow the guided activation. Within minutes it will appear here with its plan and status.',
    verifyHeading: 'Verify your email address',
    verifyBody: "We've sent a confirmation email to {email}. Click the link inside, then refresh this page to access your devices, subscriptions and orders. Can't find it? Check your spam folder.",
    verifyBodyNoEmail: "We've sent you a confirmation email. Click the link inside, then refresh this page to access your devices, subscriptions and orders. Can't find it? Check your spam folder.",
    resendPrompt: "Didn't receive the email?",
    resendAction: 'Resend it',
    resendSending: 'Sending…',
    resendSent: 'Confirmation email sent. Check your inbox.',
    resendTooMany: 'Too many requests. Please wait a few minutes before asking for a new email.',
  },
  spotlight: {
    plan: 'Plan',
    nextDue: 'Next due date',
    state: 'Status',
    lastSignal: 'Last signal',
    manage: 'Manage subscription',
  },
  plan: {
    active: 'Active plan',
    expired: 'Plan expired',
    basic: 'Basic mode',
    upgradePossible: 'Upgrade available',
    renews: 'Renews on {date}',
    expiredOn: 'Expired on {date}',
    noActivePlan: 'No active plan',
  },
  badge: { active: 'Active', expired: 'Expired', basic: 'Basic', notActivated: 'Not activated' },
  tiles: {
    subscriptions: 'Subscriptions',
    subscriptionsDesc: 'Your devices and their plans, gathered in one place.',
    orders: 'Orders',
    ordersDesc: 'Track the orders you placed on the Invoxia store.',
    invoices: 'Subscription invoices',
    invoicesDesc: 'View and download the invoices for your plans.',
    partners: 'Partners',
    partnersDesc: 'Our partners and their advantages for Invoxia customers.',
    open: 'Open',
  },
}

const fr = {
  nav: { home: 'Accueil', subscriptions: 'Abonnements', orders: 'Commandes', invoices: 'Factures', partners: 'Partenaires' },
  shell: {
    customerArea: 'Espace client',
    logout: 'Se déconnecter',
    accountMenu: 'Menu du compte',
    signedInAs: 'Connecté en tant que',
    store: 'Boutique Invoxia',
    support: 'Assistance',
  },
  footer: {
    tagline: 'Espace client Invoxia',
    terms: 'Conditions générales',
    privacy: 'Confidentialité',
    support: 'Aide & support',
  },
  home: {
    welcome: 'Bienvenue dans votre espace Invoxia',
    subtitle: 'Veillez sur ce qui compte. Retrouvez ici vos appareils, vos forfaits, vos commandes et vos factures.',
    yourDevice: 'Votre appareil',
    yourDevices: 'Vos appareils',
    manageSubscriptions: 'Gérer mes abonnements',
    quickAccess: 'Accès rapide',
    devicesTracked: 'appareils suivis',
    seeAll: 'Tout voir',
  },
  onboarding: {
    heading: 'Activez votre premier appareil',
    body: "Sortez votre tracker de sa boîte, ouvrez l'app Invoxia et suivez l'activation guidée. En quelques minutes, il apparaîtra ici avec son forfait et son état.",
    verifyHeading: 'Vérifiez votre adresse email',
    verifyBody:
      "Nous vous avons envoyé un email de confirmation à {email}. Cliquez sur le lien qu'il contient, puis actualisez cette page pour accéder à vos appareils, abonnements et commandes. Vous ne le trouvez pas ? Pensez à vérifier vos spams.",
    verifyBodyNoEmail:
      "Nous vous avons envoyé un email de confirmation. Cliquez sur le lien qu'il contient, puis actualisez cette page pour accéder à vos appareils, abonnements et commandes. Vous ne le trouvez pas ? Pensez à vérifier vos spams.",
    resendPrompt: "Vous n'avez pas reçu l'email ?",
    resendAction: "Renvoyer l'email",
    resendSending: 'Envoi en cours…',
    resendSent: 'Email de confirmation envoyé. Vérifiez votre boîte de réception.',
    resendTooMany: 'Trop de demandes. Patientez quelques minutes avant de demander un nouvel email.',
  },
  spotlight: {
    plan: 'Forfait',
    nextDue: 'Prochaine échéance',
    state: 'État',
    lastSignal: 'Dernier signal',
    manage: "Gérer l'abonnement",
  },
  plan: {
    active: 'Forfait actif',
    expired: 'Forfait expiré',
    basic: 'Mode Basic',
    upgradePossible: 'Mise à niveau possible',
    renews: 'Renouvellement le {date}',
    expiredOn: 'Expiré le {date}',
    noActivePlan: 'Aucun forfait actif',
  },
  badge: { active: 'Actif', expired: 'Expiré', basic: 'Basic', notActivated: 'Non activé' },
  tiles: {
    subscriptions: 'Abonnements',
    subscriptionsDesc: 'Vos appareils et leurs forfaits, réunis au même endroit.',
    orders: 'Commandes',
    ordersDesc: 'Suivez vos commandes passées sur la boutique Invoxia.',
    invoices: "Factures d'abonnement",
    invoicesDesc: 'Consultez et téléchargez les factures de vos forfaits.',
    partners: 'Partenaires',
    partnersDesc: 'Nos partenaires et leurs avantages pour les clients Invoxia.',
    open: 'Ouvrir',
  },
}

const de = {
  nav: { home: 'Startseite', subscriptions: 'Abonnements', orders: 'Bestellungen', invoices: 'Rechnungen', partners: 'Partner' },
  shell: {
    customerArea: 'Kundenbereich',
    logout: 'Abmelden',
    accountMenu: 'Kontomenü',
    signedInAs: 'Angemeldet als',
    store: 'Invoxia Shop',
    support: 'Support',
  },
  footer: {
    tagline: 'Invoxia Kundenbereich',
    terms: 'AGB',
    privacy: 'Datenschutz',
    support: 'Hilfe & Support',
  },
  home: {
    welcome: 'Willkommen in Ihrem Invoxia-Bereich',
    subtitle: 'Behalten Sie im Blick, was zählt. Finden Sie hier all Ihre Geräte, Tarife, Bestellungen und Rechnungen an einem Ort.',
    yourDevice: 'Ihr Gerät',
    yourDevices: 'Ihre Geräte',
    manageSubscriptions: 'Meine Abonnements verwalten',
    quickAccess: 'Schnellzugriff',
    devicesTracked: 'verfolgte Geräte',
    seeAll: 'Alle anzeigen',
  },
  onboarding: {
    heading: 'Aktivieren Sie Ihr erstes Gerät',
    body: 'Nehmen Sie Ihren Tracker aus der Verpackung, öffnen Sie die Invoxia-App und folgen Sie der geführten Aktivierung. In wenigen Minuten erscheint er hier mit seinem Tarif und Status.',
    verifyHeading: 'Bestätigen Sie Ihre E-Mail-Adresse',
    verifyBody:
      'Wir haben eine Bestätigungs-E-Mail an {email} gesendet. Klicken Sie auf den darin enthaltenen Link und laden Sie diese Seite anschließend neu, um auf Ihre Geräte, Abonnements und Bestellungen zuzugreifen. Nicht gefunden? Prüfen Sie Ihren Spam-Ordner.',
    verifyBodyNoEmail:
      'Wir haben Ihnen eine Bestätigungs-E-Mail gesendet. Klicken Sie auf den darin enthaltenen Link und laden Sie diese Seite anschließend neu, um auf Ihre Geräte, Abonnements und Bestellungen zuzugreifen. Nicht gefunden? Prüfen Sie Ihren Spam-Ordner.',
    resendPrompt: 'E-Mail nicht erhalten?',
    resendAction: 'Erneut senden',
    resendSending: 'Wird gesendet…',
    resendSent: 'Bestätigungs-E-Mail gesendet. Prüfen Sie Ihren Posteingang.',
    resendTooMany: 'Zu viele Anfragen. Warten Sie einige Minuten, bevor Sie eine neue E-Mail anfordern.',
  },
  spotlight: {
    plan: 'Tarif',
    nextDue: 'Nächste Fälligkeit',
    state: 'Status',
    lastSignal: 'Letztes Signal',
    manage: 'Abonnement verwalten',
  },
  plan: {
    active: 'Aktiver Tarif',
    expired: 'Tarif abgelaufen',
    basic: 'Basic-Modus',
    upgradePossible: 'Upgrade verfügbar',
    renews: 'Erneuert am {date}',
    expiredOn: 'Abgelaufen am {date}',
    noActivePlan: 'Kein aktiver Tarif',
  },
  badge: { active: 'Aktiv', expired: 'Abgelaufen', basic: 'Basic', notActivated: 'Nicht aktiviert' },
  tiles: {
    subscriptions: 'Abonnements',
    subscriptionsDesc: 'Ihre Geräte und deren Tarife, an einem Ort gebündelt.',
    orders: 'Bestellungen',
    ordersDesc: 'Verfolgen Sie Ihre im Invoxia Shop aufgegebenen Bestellungen.',
    invoices: 'Abonnementrechnungen',
    invoicesDesc: 'Sehen Sie Ihre Tarifrechnungen ein und laden Sie sie herunter.',
    partners: 'Partner',
    partnersDesc: 'Unsere Partner und ihre Vorteile für Invoxia-Kunden.',
    open: 'Öffnen',
  },
}

const es = {
  nav: { home: 'Inicio', subscriptions: 'Suscripciones', orders: 'Pedidos', invoices: 'Facturas', partners: 'Socios' },
  shell: {
    customerArea: 'Área de cliente',
    logout: 'Cerrar sesión',
    accountMenu: 'Menú de la cuenta',
    signedInAs: 'Conectado como',
    store: 'Tienda Invoxia',
    support: 'Soporte',
  },
  footer: {
    tagline: 'Área de cliente Invoxia',
    terms: 'Términos y condiciones',
    privacy: 'Privacidad',
    support: 'Ayuda y soporte',
  },
  home: {
    welcome: 'Bienvenido a tu espacio Invoxia',
    subtitle: 'Vigila lo que importa. Encuentra aquí tus dispositivos, planes, pedidos y facturas, todo en un solo lugar.',
    yourDevice: 'Tu dispositivo',
    yourDevices: 'Tus dispositivos',
    manageSubscriptions: 'Gestionar mis suscripciones',
    quickAccess: 'Acceso rápido',
    devicesTracked: 'dispositivos localizados',
    seeAll: 'Ver todo',
  },
  onboarding: {
    heading: 'Activa tu primer dispositivo',
    body: 'Saca tu tracker de su caja, abre la aplicación Invoxia y sigue la activación guiada. En pocos minutos aparecerá aquí con su plan y su estado.',
    verifyHeading: 'Verifica tu dirección de correo electrónico',
    verifyBody:
      'Te hemos enviado un correo de confirmación a {email}. Haz clic en el enlace que contiene y luego actualiza esta página para acceder a tus dispositivos, suscripciones y pedidos. ¿No lo encuentras? Revisa tu carpeta de spam.',
    verifyBodyNoEmail:
      'Te hemos enviado un correo de confirmación. Haz clic en el enlace que contiene y luego actualiza esta página para acceder a tus dispositivos, suscripciones y pedidos. ¿No lo encuentras? Revisa tu carpeta de spam.',
    resendPrompt: '¿No has recibido el correo?',
    resendAction: 'Reenviar el correo',
    resendSending: 'Enviando…',
    resendSent: 'Correo de confirmación enviado. Revisa tu bandeja de entrada.',
    resendTooMany: 'Demasiadas solicitudes. Espera unos minutos antes de pedir un nuevo correo.',
  },
  spotlight: {
    plan: 'Plan',
    nextDue: 'Próximo vencimiento',
    state: 'Estado',
    lastSignal: 'Última señal',
    manage: 'Gestionar suscripción',
  },
  plan: {
    active: 'Plan activo',
    expired: 'Plan expirado',
    basic: 'Modo Basic',
    upgradePossible: 'Actualización disponible',
    renews: 'Se renueva el {date}',
    expiredOn: 'Expiró el {date}',
    noActivePlan: 'Sin plan activo',
  },
  badge: { active: 'Activo', expired: 'Expirado', basic: 'Basic', notActivated: 'No activado' },
  tiles: {
    subscriptions: 'Suscripciones',
    subscriptionsDesc: 'Tus dispositivos y sus planes, reunidos en un solo lugar.',
    orders: 'Pedidos',
    ordersDesc: 'Haz seguimiento de los pedidos realizados en la tienda Invoxia.',
    invoices: 'Facturas de suscripción',
    invoicesDesc: 'Consulta y descarga las facturas de tus planes.',
    partners: 'Socios',
    partnersDesc: 'Nuestros socios y sus ventajas para los clientes de Invoxia.',
    open: 'Abrir',
  },
}

const it = {
  nav: { home: 'Home', subscriptions: 'Abbonamenti', orders: 'Ordini', invoices: 'Fatture', partners: 'Partner' },
  shell: {
    customerArea: 'Area cliente',
    logout: 'Logout',
    accountMenu: 'Menu account',
    signedInAs: 'Connesso come',
    store: 'Shop Invoxia',
    support: 'Assistenza',
  },
  footer: {
    tagline: 'Area cliente Invoxia',
    terms: 'Termini e condizioni',
    privacy: 'Privacy',
    support: 'Aiuto e supporto',
  },
  home: {
    welcome: 'Benvenuto nel tuo spazio Invoxia',
    subtitle: 'Tieni sotto controllo ciò che conta. Trova qui i tuoi dispositivi, piani, ordini e fatture, tutto in un unico posto.',
    yourDevice: 'Il tuo dispositivo',
    yourDevices: 'I tuoi dispositivi',
    manageSubscriptions: 'Gestisci i miei abbonamenti',
    quickAccess: 'Accesso rapido',
    devicesTracked: 'dispositivi monitorati',
    seeAll: 'Vedi tutto',
  },
  onboarding: {
    heading: 'Attiva il tuo primo dispositivo',
    body: "Tira fuori il tracker dalla scatola, apri l'app Invoxia e segui l'attivazione guidata. Nel giro di pochi minuti apparirà qui con il suo piano e il suo stato.",
    verifyHeading: 'Verifica il tuo indirizzo email',
    verifyBody:
      "Ti abbiamo inviato un'email di conferma a {email}. Clicca sul link che contiene, poi aggiorna questa pagina per accedere ai tuoi dispositivi, abbonamenti e ordini. Non la trovi? Controlla la cartella spam.",
    verifyBodyNoEmail:
      "Ti abbiamo inviato un'email di conferma. Clicca sul link che contiene, poi aggiorna questa pagina per accedere ai tuoi dispositivi, abbonamenti e ordini. Non la trovi? Controlla la cartella spam.",
    resendPrompt: "Non hai ricevuto l'email?",
    resendAction: 'Inviala di nuovo',
    resendSending: 'Invio in corso…',
    resendSent: 'Email di conferma inviata. Controlla la tua casella di posta.',
    resendTooMany: 'Troppe richieste. Attendi qualche minuto prima di richiedere una nuova email.',
  },
  spotlight: {
    plan: 'Piano',
    nextDue: 'Prossima scadenza',
    state: 'Stato',
    lastSignal: 'Ultimo segnale',
    manage: "Gestisci l'abbonamento",
  },
  plan: {
    active: 'Piano attivo',
    expired: 'Piano scaduto',
    basic: 'Modalità Basic',
    upgradePossible: 'Aggiornamento disponibile',
    renews: 'Rinnova il {date}',
    expiredOn: 'Scaduto il {date}',
    noActivePlan: 'Nessun piano attivo',
  },
  badge: { active: 'Attivo', expired: 'Scaduto', basic: 'Basic', notActivated: 'Non attivato' },
  tiles: {
    subscriptions: 'Abbonamenti',
    subscriptionsDesc: 'I tuoi dispositivi e i loro piani, riuniti in un unico posto.',
    orders: 'Ordini',
    ordersDesc: 'Segui gli ordini effettuati sullo shop Invoxia.',
    invoices: "Fatture dell'abbonamento",
    invoicesDesc: 'Consulta e scarica le fatture dei tuoi piani.',
    partners: 'Partner',
    partnersDesc: 'I nostri partner e i loro vantaggi per i clienti Invoxia.',
    open: 'Apri',
  },
}

const nl = {
  nav: { home: 'Home', subscriptions: 'Abonnementen', orders: 'Bestellingen', invoices: 'Facturen', partners: 'Partners' },
  shell: {
    customerArea: 'Klantenruimte',
    logout: 'Uitloggen',
    accountMenu: 'Accountmenu',
    signedInAs: 'Ingelogd als',
    store: 'Invoxia-winkel',
    support: 'Support',
  },
  footer: {
    tagline: 'Invoxia klantenruimte',
    terms: 'Gebruiksvoorwaarden',
    privacy: 'Privacy',
    support: 'Hulp & ondersteuning',
  },
  home: {
    welcome: 'Welkom in je Invoxia-ruimte',
    subtitle: 'Houd in de gaten wat telt. Vind hier al je apparaten, abonnementen, bestellingen en facturen op één plek.',
    yourDevice: 'Je apparaat',
    yourDevices: 'Je apparaten',
    manageSubscriptions: 'Mijn abonnementen beheren',
    quickAccess: 'Snelle toegang',
    devicesTracked: 'gevolgde apparaten',
    seeAll: 'Alles bekijken',
  },
  onboarding: {
    heading: 'Activeer je eerste apparaat',
    body: 'Haal je tracker uit de doos, open de Invoxia-app en volg de begeleide activering. Binnen enkele minuten verschijnt hij hier met zijn abonnement en status.',
    verifyHeading: 'Verifieer je e-mailadres',
    verifyBody:
      'We hebben een bevestigingsmail gestuurd naar {email}. Klik op de link in de e-mail en vernieuw daarna deze pagina om toegang te krijgen tot je apparaten, abonnementen en bestellingen. Niet gevonden? Kijk ook in je spammap.',
    verifyBodyNoEmail:
      'We hebben je een bevestigingsmail gestuurd. Klik op de link in de e-mail en vernieuw daarna deze pagina om toegang te krijgen tot je apparaten, abonnementen en bestellingen. Niet gevonden? Kijk ook in je spammap.',
    resendPrompt: 'Geen e-mail ontvangen?',
    resendAction: 'Opnieuw versturen',
    resendSending: 'Versturen…',
    resendSent: 'Bevestigingsmail verzonden. Controleer je inbox.',
    resendTooMany: 'Te veel aanvragen. Wacht een paar minuten voordat je een nieuwe e-mail aanvraagt.',
  },
  spotlight: {
    plan: 'Abonnement',
    nextDue: 'Volgende vervaldatum',
    state: 'Status',
    lastSignal: 'Laatste signaal',
    manage: 'Abonnement beheren',
  },
  plan: {
    active: 'Actief abonnement',
    expired: 'Abonnement verlopen',
    basic: 'Basic-modus',
    upgradePossible: 'Upgrade beschikbaar',
    renews: 'Vernieuwt op {date}',
    expiredOn: 'Verlopen op {date}',
    noActivePlan: 'Geen actief abonnement',
  },
  badge: { active: 'Actief', expired: 'Verlopen', basic: 'Basic', notActivated: 'Niet geactiveerd' },
  tiles: {
    subscriptions: 'Abonnementen',
    subscriptionsDesc: 'Je apparaten en hun abonnementen, op één plek verzameld.',
    orders: 'Bestellingen',
    ordersDesc: 'Volg de bestellingen die je hebt geplaatst in de Invoxia-winkel.',
    invoices: 'Abonnementsfacturen',
    invoicesDesc: 'Bekijk en download de facturen van je abonnementen.',
    partners: 'Partners',
    partnersDesc: 'Onze partners en hun voordelen voor Invoxia-klanten.',
    open: 'Openen',
  },
}

const pt = {
  nav: { home: 'Início', subscriptions: 'Subscrições', orders: 'Encomendas', invoices: 'Faturas', partners: 'Parceiros' },
  shell: {
    customerArea: 'Área de cliente',
    logout: 'Terminar sessão',
    accountMenu: 'Menu da conta',
    signedInAs: 'Sessão iniciada como',
    store: 'Loja Invoxia',
    support: 'Suporte',
  },
  footer: {
    tagline: 'Área de cliente Invoxia',
    terms: 'Termos e condições',
    privacy: 'Privacidade',
    support: 'Ajuda e apoio',
  },
  home: {
    welcome: 'Bem-vindo ao seu espaço Invoxia',
    subtitle: 'Não perca de vista o que importa. Encontre aqui os seus dispositivos, planos, encomendas e faturas, tudo num só lugar.',
    yourDevice: 'O seu dispositivo',
    yourDevices: 'Os seus dispositivos',
    manageSubscriptions: 'Gerir as minhas subscrições',
    quickAccess: 'Acesso rápido',
    devicesTracked: 'dispositivos localizados',
    seeAll: 'Ver tudo',
  },
  onboarding: {
    heading: 'Ative o seu primeiro dispositivo',
    body: 'Retire o seu localizador da caixa, abra a aplicação Invoxia e siga a ativação guiada. Em poucos minutos, aparecerá aqui com o respetivo plano e estado.',
    verifyHeading: 'Verifique o seu endereço de e-mail',
    verifyBody:
      'Enviámos um e-mail de confirmação para {email}. Clique na ligação incluída no e-mail e, em seguida, atualize esta página para aceder aos seus dispositivos, subscrições e encomendas. Não o encontra? Verifique a pasta de spam.',
    verifyBodyNoEmail:
      'Enviámos-lhe um e-mail de confirmação. Clique na ligação incluída no e-mail e, em seguida, atualize esta página para aceder aos seus dispositivos, subscrições e encomendas. Não o encontra? Verifique a pasta de spam.',
    resendPrompt: 'Não recebeu o e-mail?',
    resendAction: 'Reenviar o e-mail',
    resendSending: 'A enviar…',
    resendSent: 'E-mail de confirmação enviado. Verifique a sua caixa de entrada.',
    resendTooMany: 'Demasiados pedidos. Aguarde alguns minutos antes de pedir um novo e-mail.',
  },
  spotlight: {
    plan: 'Plano',
    nextDue: 'Próximo vencimento',
    state: 'Estado',
    lastSignal: 'Último sinal',
    manage: 'Gerir subscrição',
  },
  plan: {
    active: 'Plano ativo',
    expired: 'Plano expirado',
    basic: 'Modo Basic',
    upgradePossible: 'Melhoria disponível',
    renews: 'Renova a {date}',
    expiredOn: 'Expirou a {date}',
    noActivePlan: 'Nenhum plano ativo',
  },
  badge: { active: 'Ativo', expired: 'Expirado', basic: 'Basic', notActivated: 'Não ativado' },
  tiles: {
    subscriptions: 'Subscrições',
    subscriptionsDesc: 'Os seus dispositivos e os respetivos planos, reunidos num só lugar.',
    orders: 'Encomendas',
    ordersDesc: 'Acompanhe as encomendas feitas na loja Invoxia.',
    invoices: 'Faturas de subscrição',
    invoicesDesc: 'Consulte e descarregue as faturas dos seus planos.',
    partners: 'Parceiros',
    partnersDesc: 'Os nossos parceiros e as suas vantagens para os clientes Invoxia.',
    open: 'Abrir',
  },
}

const cs = {
  nav: { home: 'Domů', subscriptions: 'Předplatné', orders: 'Objednávky', invoices: 'Faktury', partners: 'Partneři' },
  shell: {
    customerArea: 'Zákaznická sekce',
    logout: 'Odhlásit se',
    accountMenu: 'Nabídka účtu',
    signedInAs: 'Přihlášen jako',
    store: 'Obchod Invoxia',
    support: 'Podpora',
  },
  footer: {
    tagline: 'Zákaznická sekce Invoxia',
    terms: 'Podmínky použití',
    privacy: 'Soukromí',
    support: 'Nápověda a podpora',
  },
  home: {
    welcome: 'Vítejte ve vašem prostoru Invoxia',
    subtitle: 'Mějte přehled o tom, na čem záleží. Najdete zde svá zařízení, předplatné, objednávky a faktury, vše na jednom místě.',
    yourDevice: 'Vaše zařízení',
    yourDevices: 'Vaše zařízení',
    manageSubscriptions: 'Spravovat moje předplatné',
    quickAccess: 'Rychlý přístup',
    devicesTracked: 'sledovaných zařízení',
    seeAll: 'Zobrazit vše',
  },
  onboarding: {
    heading: 'Aktivujte své první zařízení',
    body: 'Vyjměte tracker z krabice, otevřete aplikaci Invoxia a projděte průvodce aktivací. Během několika minut se zde objeví s předplatným a stavem.',
    verifyHeading: 'Ověřte svou e-mailovou adresu',
    verifyBody:
      'Poslali jsme vám potvrzovací e-mail na {email}. Klikněte na odkaz v něm, poté obnovte tuto stránku a získáte přístup ke svým zařízením, předplatným a objednávkám. Nemůžete ho najít? Zkontrolujte složku se spamem.',
    verifyBodyNoEmail:
      'Poslali jsme vám potvrzovací e-mail. Klikněte na odkaz v něm, poté obnovte tuto stránku a získáte přístup ke svým zařízením, předplatným a objednávkám. Nemůžete ho najít? Zkontrolujte složku se spamem.',
    resendPrompt: 'E-mail nedorazil?',
    resendAction: 'Odeslat znovu',
    resendSending: 'Odesílání…',
    resendSent: 'Potvrzovací e-mail byl odeslán. Zkontrolujte svou doručenou poštu.',
    resendTooMany: 'Příliš mnoho požadavků. Než požádáte o nový e-mail, vyčkejte několik minut.',
  },
  spotlight: {
    plan: 'Předplatné',
    nextDue: 'Příští platba',
    state: 'Stav',
    lastSignal: 'Poslední signál',
    manage: 'Spravovat předplatné',
  },
  plan: {
    active: 'Aktivní předplatné',
    expired: 'Předplatné vypršelo',
    basic: 'Basic režim',
    upgradePossible: 'Upgrade dostupný',
    renews: 'Obnovuje se dne {date}',
    expiredOn: 'Vypršelo dne {date}',
    noActivePlan: 'Žádné aktivní předplatné',
  },
  badge: { active: 'Aktivní', expired: 'Vypršelo', basic: 'Basic', notActivated: 'Neaktivováno' },
  tiles: {
    subscriptions: 'Předplatné',
    subscriptionsDesc: 'Vaše zařízení a jejich předplatné, na jednom místě.',
    orders: 'Objednávky',
    ordersDesc: 'Sledujte objednávky zadané v obchodě Invoxia.',
    invoices: 'Faktury za předplatné',
    invoicesDesc: 'Zobrazte a stáhněte si faktury ke svému předplatnému.',
    partners: 'Partneři',
    partnersDesc: 'Naši partneři a jejich výhody pro zákazníky Invoxia.',
    open: 'Otevřít',
  },
}

const ja = {
  nav: { home: 'ホーム', subscriptions: 'サブスクリプション', orders: '注文', invoices: '請求書', partners: 'パートナー' },
  shell: {
    customerArea: 'カスタマーエリア',
    logout: 'ログアウト',
    accountMenu: 'アカウントメニュー',
    signedInAs: 'ログイン中のアカウント',
    store: 'Invoxiaショップ',
    support: 'サポート',
  },
  footer: {
    tagline: 'Invoxiaカスタマーエリア',
    terms: '利用規約',
    privacy: 'プライバシー',
    support: 'ヘルプ＆サポート',
  },
  home: {
    welcome: 'Invoxiaスペースへようこそ',
    subtitle: '大切なものをいつでも確認。デバイス、プラン、注文、請求書をまとめてここでチェックできます。',
    yourDevice: 'あなたのデバイス',
    yourDevices: 'あなたのデバイス',
    manageSubscriptions: 'サブスクリプションを管理する',
    quickAccess: 'クイックアクセス',
    devicesTracked: '台のデバイスを追跡中',
    seeAll: 'すべて見る',
  },
  onboarding: {
    heading: '最初のデバイスを有効化しましょう',
    body: 'トラッカーを箱から取り出し、Invoxiaアプリを開いてガイドに従って有効化してください。数分でプランとステータスとともにここに表示されます。',
    verifyHeading: 'メールアドレスを確認してください',
    verifyBody:
      '{email} 宛てに確認メールをお送りしました。メール内のリンクをクリックしてから、このページを再読み込みすると、デバイス、サブスクリプション、注文にアクセスできます。見つからない場合は、迷惑メールフォルダもご確認ください。',
    verifyBodyNoEmail:
      '確認メールをお送りしました。メール内のリンクをクリックしてから、このページを再読み込みすると、デバイス、サブスクリプション、注文にアクセスできます。見つからない場合は、迷惑メールフォルダもご確認ください。',
    resendPrompt: 'メールが届いていませんか？',
    resendAction: '再送する',
    resendSending: '送信中…',
    resendSent: '確認メールを送信しました。受信トレイをご確認ください。',
    resendTooMany: 'リクエストが多すぎます。数分待ってから、もう一度お試しください。',
  },
  spotlight: {
    plan: 'プラン',
    nextDue: '次回のお支払い日',
    state: 'ステータス',
    lastSignal: '最終通信',
    manage: 'サブスクリプションを管理',
  },
  plan: {
    active: '利用中のプラン',
    expired: 'プランの期限切れ',
    basic: 'Basicモード',
    upgradePossible: 'アップグレード可能',
    renews: '{date}に更新',
    expiredOn: '{date}に期限切れ',
    noActivePlan: '有効なプランがありません',
  },
  badge: { active: 'アクティブ', expired: '期限切れ', basic: 'Basic', notActivated: '有効化されていません' },
  tiles: {
    subscriptions: 'サブスクリプション',
    subscriptionsDesc: 'デバイスとそのプランを、まとめて確認できます。',
    orders: '注文',
    ordersDesc: 'Invoxiaショップでの注文状況を確認できます。',
    invoices: 'サブスクリプションの請求書',
    invoicesDesc: 'プランの請求書を確認・ダウンロードできます。',
    partners: 'パートナー',
    partnersDesc: 'パートナー企業と、Invoxia のお客様向けの特典。',
    open: '開く',
  },
}

export const homeMessages = { en, fr, de, es, it, nl, pt, cs, ja }
